import { Grid } from "@mui/material";
import AddressSection from "@/components/app/admin/provider-groups/AddressSection";
import FormSectionCard from "@/components/app/admin/provider-groups/FormSectionCard";
import ConfirmationModal from "@/components/app/ConfirmationModal";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import CustomSelect from "@/components/CustomSelect";
import Input from "@/components/Input";
import SideSheetDrawer from "@/components/SidesheetDrawer";
import { usePayerForm } from "../hooks/use-payer-form";
import {
  ADDRESS_LINE_2_FIELD,
  PAYER_FIELD_LABEL,
  PAYER_MESSAGES,
  PAYER_TITLE,
  PAYER_TYPE_OPTIONS,
  PayerFormValues,
} from "../payers-constant";

export interface UpsertPayerDrawerProps {
  open: boolean;
  onClose: () => void;
  preFillValues?: PayerFormValues;
}

const DRAWER_WIDTH = "60vw";

const UpsertPayerDrawer = ({
  open,
  onClose,
  preFillValues,
}: UpsertPayerDrawerProps) => {
  const { formik, isEditMode, discard, warning, onConfirmWarning } =
    usePayerForm({ preFillValues, onSaved: onClose });
  const { values, errors, touched, handleChange, handleBlur } = formik;

  const handleClose = () => {
    if (formik.dirty) {
      discard.open();
      return;
    }
    onClose();
  };

  const renderInput = (
    name: "payerId" | "payerName",
    label: string,
    maxLength: number
  ) => (
    <Input
      required
      label={label}
      textFieldProps={{
        name,
        value: values[name],
        onChange: handleChange,
        onBlur: handleBlur,
        placeholder: `Enter ${label}`,
        inputProps: { maxLength, "aria-label": label },
      }}
      error={touched[name] && errors[name]}
    />
  );

  return (
    <SideSheetDrawer
      open={open}
      onClose={handleClose}
      title={isEditMode ? PAYER_TITLE.EDIT : PAYER_TITLE.ADD}
      w={DRAWER_WIDTH}
      footerActions={
        <StyledButtonV2
          label="Save"
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid || formik.isSubmitting}
        />
      }
    >
      <Grid container spacing={2}>
        <FormSectionCard title={PAYER_TITLE.PAYER_DETAILS}>
          <Grid item xs={12} md={6}>
            {renderInput("payerId", PAYER_FIELD_LABEL.PAYER_ID, 20)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderInput("payerName", PAYER_FIELD_LABEL.PAYER_NAME, 150)}
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomSelect
              required
              clearable={false}
              label={PAYER_FIELD_LABEL.PAYER_TYPE}
              placeholder="Select"
              name="payerType"
              options={PAYER_TYPE_OPTIONS}
              value={values.payerType}
              onChange={(value) =>
                formik.setFieldValue("payerType", value ?? "")
              }
              handleBlur={handleBlur}
              error={touched.payerType && errors.payerType}
            />
          </Grid>
        </FormSectionCard>

        <AddressSection
          formik={formik}
          formikKey="address"
          title={PAYER_TITLE.ADDRESS}
          required
          optionalFields={[ADDRESS_LINE_2_FIELD]}
        />
      </Grid>

      <ConfirmationModal
        disclosureHost={discard}
        content={PAYER_MESSAGES.DISCARD}
        confirmationText="Discard"
        onConfirm={() => {
          discard.close();
          onClose();
        }}
      />
      <ConfirmationModal
        disclosureHost={warning}
        content={warning.data?.message}
        confirmationText="Continue"
        onConfirm={onConfirmWarning}
      />
    </SideSheetDrawer>
  );
};

export default UpsertPayerDrawer;
